import { useMemo } from 'react';
import Select from 'react-select';

import type { MultiValue, StylesConfig } from 'react-select';

interface MultiSelectFilterOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps<T extends MultiSelectFilterOption> {
  options: readonly T[];
  value: T[];
  onChange: (value: MultiValue<T>) => void;
  placeholder?: string;
  label?: string;
  ariaLabel?: string;
  classNamePrefix?: string;
}

export function MultiSelectFilter<T extends MultiSelectFilterOption>({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  ariaLabel,
  classNamePrefix,
}: MultiSelectFilterProps<T>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectStyles = useMemo<StylesConfig<any, true>>(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 40,
        backgroundColor: 'var(--color-background)',
        borderColor: state.isFocused
          ? 'var(--color-ring)'
          : 'var(--color-border)',
        boxShadow: state.isFocused ? '0 0 0 1px var(--color-ring)' : 'none',
        '&:hover': {
          borderColor: 'var(--color-ring)',
        },
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: 'var(--color-secondary)',
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: 'var(--color-secondary-foreground)',
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: 'var(--color-secondary-foreground)',
        ':hover': {
          backgroundColor: 'var(--color-destructive)',
          color: 'var(--color-destructive-foreground)',
        },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: 'var(--color-card)',
        border: `1px solid var(--color-border)`,
        boxShadow: 'var(--shadow-lg, 0 10px 15px -3px rgba(0,0,0,0.1))',
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? 'var(--color-primary)'
          : state.isFocused
            ? 'var(--color-accent)'
            : 'var(--color-card)',
        color: state.isSelected
          ? 'var(--color-primary-foreground)'
          : 'var(--color-card-foreground)',
        cursor: 'pointer',
      }),
      placeholder: (base) => ({
        ...base,
        color: 'var(--color-muted-foreground)',
      }),
      input: (base) => ({
        ...base,
        color: 'var(--color-foreground)',
      }),
      singleValue: (base) => ({
        ...base,
        color: 'var(--color-foreground)',
      }),
      dropdownIndicator: (base) => ({
        ...base,
        color: 'var(--color-muted-foreground)',
      }),
      clearIndicator: (base) => ({
        ...base,
        color: 'var(--color-muted-foreground)',
      }),
    }),
    []
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:min-w-[220px]">
      {label && (
        <label className="text-xs font-semibold uppercase text-muted-foreground">
          {label}
        </label>
      )}
      <Select<T, true>
        isMulti
        options={options as T[]}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
        classNamePrefix={classNamePrefix}
        styles={selectStyles}
        menuPlacement="auto"
      />
    </div>
  );
}
